import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useEffect, useState } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

// Context and Utils
import { useTheme } from '../context/ThemeContext';
import { useMissions } from '../context/MissionsContext';
import { useNotifications } from '../context/NotificationsContext';
import { useAuth } from '../context/AuthContext';
import { registerWebPush, webPushSupport } from '../push/webpush';
import { track } from '../insights/beacon';
import { getThemeColors } from '../utils/themeStyles';
import { navigateNotificationRoute } from '../utils/notificationRoute';
import { navigateFromAnywhere } from '../navigation/navRef';
import ErrorRetry from '../components/ErrorRetry';
import ActionLoader from '../components/ActionLoader';
import { useT } from '../i18n/useT';
import type { MessageKey } from '../i18n';

type NotifType = 'chapter' | 'chat' | 'quiz' | 'card' | 'badge' | 'leaderboard' | 'mission' | 'system';

interface Notification {
  id: number;
  type: NotifType;
  title: string;
  message: string;
  time: string;
  unread: boolean;
  icon: string;
  color: string;
  senderName?: string | null;
  senderAvatar?: string | null;
  senderColor?: string | null;
  missionTitle?: string | null;
}

// CCT-124 — readable label for the notification's nature: the pill used to show the technical
// key (« card », « chapter »), which means nothing to a player.
//
// ⚠️ KEYS, NOT TRANSLATED STRINGS. This map is built once at import time, before any hook has
// run; a translated string here would freeze the language loaded first and leave these pills
// untranslated while the rest of the screen followed `?lang=`.
const TYPE_LABEL: Record<string, MessageKey> = {
  chapter: 'notif.type.chapter', chat: 'notif.type.chat', quiz: 'notif.type.quiz',
  card: 'notif.type.card', mission: 'notif.type.mission', badge: 'notif.type.badge',
  leaderboard: 'notif.type.leaderboard', system: 'notif.type.system',
  reminder: 'notif.type.reminder', story: 'notif.type.story',
};

const TYPE_ICON: Record<string, string> = {
  chapter: 'book-open', chat: 'chatbox', quiz: 'brain', card: 'card',
  mission: 'trophy', badge: 'trophy', leaderboard: 'trending-up', system: 'notifications',
  // `reminder` et `story` sont ÉMIS par le moteur : absents d'ici, ils retombaient sur
  // « Système » — un rappel de chapitre s'affichait donc comme une notification système.
  reminder: 'notifications', story: 'book-open',
};

// The translate function is an ARGUMENT: this helper lives outside the component, so it cannot
// call a hook, and reading a language from global state would freeze it at import time.
function relativeTime(iso: string | null, t: (k: MessageKey, v?: Record<string, unknown>) => string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return t('notif.justNow');
  if (min < 60) return t('notif.ago.min', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('notif.ago.hour', { n: h });
  const d = Math.floor(h / 24);
  if (d < 30) return t('notif.ago.day', { n: d });
  return new Date(iso).toLocaleDateString();
}

/**
 * `onOpened` — CCT-346. Called when the player OPENS a notification, whatever follows.
 *
 * ⚠️ IT CANNOT BE DEDUCED FROM THE NAVIGATION, and that is why it exists. This screen routes
 * through `navigateFromAnywhere` (the global nav ref, CCT-149) and not through its own
 * `navigation`: a caller that renders it inside a panel therefore never sees the departure, and
 * its panel stayed open ON TOP of the screen that had just been opened underneath. A notification
 * carrying no route navigates nowhere at all — and must still close the panel.
 */
export default function NotificationsScreen({ onOpened }: { onOpened?: () => void } = {}) {
  const t = useT();
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  
  const { cartItems } = useMissions();
  const { items, loading, error, refresh, markRead, markAllRead } = useNotifications();
  const { getAccessToken } = useAuth();
  const [filter, setFilter] = useState('all');
  // CCT-233 — RAFRAÎCHISSEMENT MANUEL. Le poll de 45 s a été supprimé : il représentait
  // ~32 appels/heure et maintenait le backend allumé 53 % du temps. Le centre se met donc
  // à jour sur événement (push, retour de focus) et, à la demande, avec ce bouton — sans
  // lui, l'écran n'aurait plus AUCUN moyen de se rafraîchir.
  const [refreshing, setRefreshing] = useState(false);
  const onManualRefresh = async () => {
    if (refreshing) return;   // évite d'empiler les appels sur des clics répétés
    setRefreshing(true);
    // Clic EXPLICITE sur « rafraîchir » : on l'enregistre comme action cliente, sinon l'appel
    // qu'il déclenche se rattacherait à la dernière navigation — une cause approximative là où
    // on connaît la vraie (CCT-259).
    track({ type: 'action', path: '/Notifications', action: 'refresh' });
    try { await refresh(); } finally { setRefreshing(false); }
  };
  // Web push : activer les notifications de CE navigateur (geste utilisateur requis
  // pour que le prompt s'affiche → bouton dédié plutôt qu'un appel automatique).
  const [permStatus, setPermStatus] = useState<string>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const enableNotifs = async () => {
    // `force` : c'est un geste EXPLICITE de l'utilisateur (« Activer »). Il doit refaire le
    // tour complet même si l'abonnement a déjà été déclaré dans cette page — sinon le bouton
    // ne ferait rien de visible et paraîtrait cassé (CCT-259).
    await registerWebPush(getAccessToken(), { force: true });
    if (typeof Notification !== 'undefined') setPermStatus(Notification.permission);
  };
  // Ré-aligne l'affichage sur l'état RÉEL de la permission à l'ouverture de l'écran
  // (elle a pu changer dans les réglages du navigateur entre-temps).
  useEffect(() => {
    if (typeof Notification !== 'undefined') setPermStatus(Notification.permission);
  }, []);

  const getNotificationColor = (type: string) => {
    return colors.notificationColors[type as keyof typeof colors.notificationColors] || colors.textSecondary;
  };

  // API -> forme d'affichage (icône/couleur/temps relatif, dépendants du thème).
  const notifications: Notification[] = items.map(n => {
    const type = (TYPE_ICON[n.type] ? n.type : 'system') as NotifType;
    return {
      id: n.id,
      type,
      title: n.title,
      message: n.message,
      time: relativeTime(n.createdAt, t),
      unread: n.unread,
      icon: TYPE_ICON[type] || 'notifications',
      color: getNotificationColor(type),
      senderName: n.senderName,
      senderAvatar: n.senderAvatar,
      senderColor: n.senderColor,
      missionTitle: n.missionTitle,
    };
  });

  const filteredNotifications = filter === 'all'
    ? notifications
    : notifications.filter(n => n.type === filter);

  const unreadCount = notifications.filter(n => n.unread).length;

  const markAllAsRead = () => { void markAllRead(); };

  // Deep-link au clic : navigue vers la route portée par la notification.
  // La logique est MUTUALISÉE avec l'ouverture d'une notification navigateur (CCT-97) —
  // cf. utils/notificationRoute : une route ajoutée côté moteur ne doit être traitée qu'à un seul endroit.
  // CCT-149 : on passe par la MÊME porte que la notification navigateur (`navigateFromAnywhere`),
  // qui sait viser le stack interne du layout desktop. Utiliser la navigation LOCALE ouvrait la
  // cible à la racine — même écran, mais hors du menu, d'où l'incohérence entre les deux chemins.
  const navigateRoute = (route?: string | null) => {
    navigateNotificationRoute({ navigate: (n: any, p: any) => navigateFromAnywhere(n, p) }, route);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.unread) void markRead(notification.id);
    // ⚠️ BEFORE the navigation, and OUTSIDE any condition on the route: the panel that shows this
    // screen must close on the gesture, not on its consequence (see `onOpened` above).
    onOpened?.();
    navigateRoute(items.find(i => i.id === notification.id)?.route);
  };

  const getFilterCounts = () => {
    const counts = {
      all: notifications.length,
      chat: notifications.filter(n => n.type === 'chat').length,
      chapter: notifications.filter(n => n.type === 'chapter').length,
      quiz: notifications.filter(n => n.type === 'quiz').length,
      card: notifications.filter(n => n.type === 'card').length,
      mission: notifications.filter(n => n.type === 'mission').length,
      badge: notifications.filter(n => n.type === 'badge').length,
      leaderboard: notifications.filter(n => n.type === 'leaderboard').length,
      system: notifications.filter(n => n.type === 'system').length
    };
    return counts;
  };

  const counts = getFilterCounts();

  const renderFilterTab = (filterType: string, label: string, count: number) => (
    <TouchableOpacity
      key={filterType}
      style={[
        styles.filterTab,
        { backgroundColor: filter === filterType ? colors.primary : colors.surfaceSecondary }
      ]}
      onPress={() => setFilter(filterType)}
    >
      <Text style={[
        styles.filterTabText,
        { color: filter === filterType ? colors.badgeText : colors.textSecondary }
      ]}>
        {label} ({count})
      </Text>
    </TouchableOpacity>
  );

  const renderNotification = (notification: Notification) => {
    const getIconName = (icon: string) => {
      switch (icon) {
        case 'book-open': return 'book';
        case 'chatbox': return 'chatbox';
        case 'brain': return 'bulb';
        case 'card': return 'card';
        case 'trophy': return 'trophy';
        case 'trending-up': return 'trending-up';
        case 'notifications': return 'notifications';
        default: return 'notifications';
      }
    };

    return (
      <TouchableOpacity 
        key={notification.id} 
        style={[
          styles.notificationCard,
          {
            // Aligné sur le DASHBOARD (kanban) : carte SOMBRE `cardBackground` + bord fin, jamais
            // d'aplat bleu plein. Le « non lu » se signale par un liseré ACCENT à gauche (+ la
            // pastille), pas par un fond saturé qui écrasait le contenu et jurait avec le reste.
            backgroundColor: colors.cardBackground,
            shadowColor: colors.cardShadow,
            borderWidth: 1,
            borderColor: colors.border,
            borderLeftColor: notification.unread ? colors.unreadBorder : colors.border,
            borderLeftWidth: notification.unread ? 4 : 1,
          }
        ]}
        onPress={() => handleNotificationClick(notification)}
      >
        <View style={styles.notificationContent}>
                          <View style={styles.notificationIconContainer}>
              {notification.senderAvatar ? (
                <Image source={{ uri: notification.senderAvatar }} style={styles.iconBackground} resizeMode="cover" />
              ) : notification.senderName ? (
                <View style={[styles.iconBackground, { backgroundColor: notification.senderColor || notification.color, alignItems: 'center', justifyContent: 'center' }]}>
                  <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>
                    {notification.senderName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('')}
                  </Text>
                </View>
              ) : (
                <View style={[styles.iconBackground, { backgroundColor: `${notification.color}20` }]}>
                  <Ionicons
                    name={getIconName(notification.icon) as any}
                    size={16}
                    color={notification.color}
                  />
                </View>
              )}
          </View>
          
          <View style={styles.notificationText}>
            {notification.senderName ? (
              <Text style={{ fontSize: 12, fontWeight: '700', color: notification.senderColor || colors.textSecondary, marginBottom: 2 }}>
                {notification.senderName}
              </Text>
            ) : null}
            <View style={styles.notificationHeader}>
              <Text style={[
                styles.notificationTitle,
                { color: notification.unread ? colors.textPrimary : colors.textSecondary },
                notification.unread && styles.unreadTitle
              ]}>
                {notification.title}
              </Text>
              {notification.unread && (
                <View style={[styles.unreadDot, { backgroundColor: notification.color }]} />
              )}
            </View>
            
            <Text style={[
              styles.notificationMessage,
              { color: notification.unread ? colors.textPrimary : colors.textSecondary }
            ]}>
              {notification.message}
            </Text>
            
            <View style={styles.notificationFooter}>
              <Text style={[styles.timestamp, { color: colors.textTertiary }]}>
                {notification.time}
              </Text>
              {/* Tag MISSION : sans lui, deux missions en cours donnaient des notifications
                  indiscernables (« Étape validée » — laquelle ?). */}
              {!!notification.missionTitle && (
                <View style={[styles.typeBadge, { borderColor: colors.borderSecondary }]}>
                  <Ionicons name="flag-outline" size={10} color={colors.textTertiary} />
                  <Text numberOfLines={1} style={[styles.typeBadgeText, { color: colors.textSecondary, maxWidth: 150 }]}>
                    {notification.missionTitle}
                  </Text>
                </View>
              )}
              {/* Pastille de NATURE, à la couleur du type d'élément. */}
              <View style={[styles.typeBadge, { borderColor: notification.color, backgroundColor: `${notification.color}1F` }]}>
                <Ionicons name={getIconName(notification.icon) as any} size={10} color={notification.color} />
                <Text style={[styles.typeBadgeText, { color: notification.color }]}>
                  {TYPE_LABEL[notification.type] ? t(TYPE_LABEL[notification.type]) : notification.type}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <View style={styles.iconContainer}>
              <Ionicons name="notifications" size={24} color={colors.iconPrimary} />
              {unreadCount > 0 && (
                <View style={[styles.badge, { backgroundColor: colors.badgeBackground }]}>
                  <Text style={[styles.badgeText, { color: colors.badgeText }]}>{unreadCount}</Text>
                </View>
              )}
            </View>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('notif.title')}</Text>
          </View>
          <TouchableOpacity
            style={styles.cartButton}
            onPress={onManualRefresh}
            disabled={refreshing}
            accessibilityRole="button"
            accessibilityLabel={t('notif.refresh')}
          >
            <Ionicons
              name={refreshing ? 'sync' : 'refresh'}
              size={20}
              color={refreshing ? colors.textTertiary : colors.iconPrimary}
            />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.cartButton}
            onPress={() => navigation.navigate('Cart' as never)}
          >
            <Ionicons name="cart" size={20} color={colors.iconPrimary} />
            {cartItems.length > 0 && (
              <View style={[styles.cartBadge, { backgroundColor: colors.badgeBackground }]}>
                <Text style={[styles.cartBadgeText, { color: colors.badgeText }]}>{cartItems.length}</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
        
        {unreadCount > 0 && (
          <View style={styles.unreadSection}>
            <Text style={[styles.unreadText, { color: colors.textSecondary }]}>
              You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Text>
            <TouchableOpacity 
              style={[styles.markAllButton, { borderColor: colors.borderSecondary }]} 
              onPress={markAllAsRead}
            >
              <Text style={[styles.markAllButtonText, { color: colors.textSecondary }]}>
                {t('notif.markAllRead')}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

             {/* Filter Tabs */}
       <View style={[styles.filtersContainer, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
         <View style={styles.filterHeader}>
           <Ionicons name="filter" size={16} color={colors.iconSecondary} />
           <Text style={[styles.filterTitle, { color: colors.textPrimary }]}>{t('notif.filterByType')}</Text>
         </View>
         <View style={styles.filterSection}>
           <View style={styles.filterOptions}>
             {renderFilterTab('all', t('notif.filter.all'), counts.all)}
             {renderFilterTab('chat', t('notif.filter.chat'), counts.chat)}
             {renderFilterTab('chapter', t('notif.filter.chapters'), counts.chapter)}
             {renderFilterTab('quiz', t('notif.filter.quizzes'), counts.quiz)}
             {renderFilterTab('card', t('notif.filter.cards'), counts.card)}
           </View>
         </View>
       </View>

      {/* Notifications List */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.notificationsList}>
          {Platform.OS === 'web' && permStatus === 'default' && (
            <TouchableOpacity onPress={enableNotifs}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, marginBottom: 10,
                borderRadius: 12, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surfaceSecondary }}>
              <Ionicons name="notifications-outline" size={18} color={colors.primary} />
              <Text style={{ flex: 1, color: colors.textPrimary, fontSize: 13 }}>
                {t('notif.enablePrompt')}
              </Text>
              <Text style={{ color: colors.primary, fontWeight: '700', fontSize: 13 }}>{t('notif.enable')}</Text>
            </TouchableOpacity>
          )}
          {Platform.OS === 'web' && permStatus === 'granted' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 10, marginBottom: 10,
              borderRadius: 12, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surfaceSecondary }}>
              <Ionicons name="notifications" size={16} color={colors.primary} />
              <Text style={{ color: colors.textSecondary, fontSize: 12 }}>{t('notif.enabled')}</Text>
            </View>
          )}
          {/* CCT-115: on iPhone/iPad, Safari in a TAB cannot receive push at all (Apple only
              allows it after « Add to Home Screen »). Without this message, enabling notifications
              did nothing at all, with no explanation. */}
          {Platform.OS === 'web' && webPushSupport() === 'ios-needs-install' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, marginBottom: 10, borderRadius: 12,
              borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surfaceSecondary }}>
              <Ionicons name="phone-portrait-outline" size={18} color={colors.textSecondary} />
              <Text style={{ flex: 1, color: colors.textSecondary, fontSize: 12 }}>
                {t('notif.iosHomeScreen')}
              </Text>
            </View>
          )}
          {Platform.OS === 'web' && permStatus === 'denied' && webPushSupport() !== 'ios-needs-install' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, marginBottom: 10, borderRadius: 12,
              borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surfaceSecondary }}>
              <Ionicons name="notifications-off-outline" size={18} color={colors.textSecondary} />
              <Text style={{ flex: 1, color: colors.textSecondary, fontSize: 12 }}>
                {t('notif.blocked')}
              </Text>
            </View>
          )}
          {error && items.length === 0 ? (
            <ErrorRetry onRetry={() => void refresh()} retrying={loading} message={t('notif.loadFailed')} />
          ) : loading && items.length === 0 ? (
            <View style={styles.emptyState}>
              <ActionLoader label={t('notif.loading')} />
            </View>
          ) : filteredNotifications.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="notifications" size={48} color={colors.textTertiary} />
              <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>{t('notif.empty')}</Text>
              <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>{t('notif.emptySub')}</Text>
            </View>
          ) : (
            filteredNotifications.map(renderNotification)
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    borderBottomWidth: 1,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    position: 'relative',
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -6,
    borderRadius: 12,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  cartButton: {
    position: 'relative',
    padding: 8,
  },
  cartBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  unreadSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  unreadText: {
    fontSize: 14,
  },
  markAllButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 6,
  },
  markAllButtonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  filtersContainer: {
    padding: 16,
    borderBottomWidth: 1,
  },
  filterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  filterTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  filterSection: {
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterTab: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  activeFilterTab: {
    // Active state handled by dynamic styles
  },
  filterTabText: {
    fontSize: 11,
    fontWeight: '500',
  },
  activeFilterTabText: {
    // Active state handled by dynamic styles
  },
  content: {
    flex: 1,
  },
  notificationsList: {
    padding: 16,
  },
  notificationCard: {
    borderRadius: 12,
    // Liseré d'accent gauche RECTILIGNE (non lu) : coins gauches carrés (cf. squareLeftCorners).
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    padding: 16,
    marginBottom: 12,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  notificationContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  notificationIconContainer: {
    marginRight: 12,
  },
  iconBackground: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationText: {
    flex: 1,
  },
  notificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  notificationTitle: {
    fontSize: 16,
    flex: 1,
    marginRight: 8,
  },
  unreadTitle: {
    fontWeight: '600',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  notificationMessage: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  notificationFooter: {
    flexDirection: 'row',
    // `wrap` + `gap` : avec deux tags (mission + nature), un `space-between` sur une seule
    // ligne écrasait le titre de mission sur les cartes étroites.
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
  },
  timestamp: {
    fontSize: 12,
    marginRight: 'auto' as any,   // horodatage à gauche, tags à droite
  },
  typeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderWidth: 1,
    borderRadius: 4,
  },
  typeBadgeText: {
    fontSize: 10,
    textTransform: 'capitalize',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
  },
}); 